import { z } from "zod";
import { discordSnowflakeSchema, utcTimestampSchema, } from "./guild-settings.js";
import { entitlementEffectiveStateSchema, guildEntitlementProjectionSchema, } from "./guild-entitlements.js";
import { guildLeaderRecoverySchema, guildOwnershipSchema, } from "./guild-leadership.js";
import { boundedNfcTextSchema, normalizedSnowflakeListSchema, opaqueCursorSchema, sanitizedJsonObjectSchema, unicodeCodePointLength, uuidSchema, } from "./platform-common.js";
import { wowauditConnectionStatusSchema } from "./wowaudit.js";
export const MAX_SUPERADMIN_DISCORD_USER_IDS = 8;
export const superadminDiscordUserIdsSchema = normalizedSnowflakeListSchema(MAX_SUPERADMIN_DISCORD_USER_IDS, "Superadmin Discord user IDs");
export const adminMutationReasonSchema = boundedNfcTextSchema(500, 2_048);
export const adminRequestIdSchema = z
    .string()
    .trim()
    .min(1)
    .refine((value) => unicodeCodePointLength(value) <= 128, {
    message: "Request ID must contain at most 128 Unicode code points",
});
export const superadminSessionCapabilitySchema = z
    .object({
    authorized: z.boolean(),
    accessRequired: z.boolean(),
    accessSatisfied: z.boolean(),
})
    .strict()
    .refine((value) => !value.authorized || !value.accessRequired || value.accessSatisfied, "An authorized session must satisfy required Access verification");
const adminGuildEntitlementMutationBase = {
    expectedRevision: z.int().positive(),
    reason: adminMutationReasonSchema,
};
export const adminGuildEntitlementMutationSchema = z.discriminatedUnion("action", [
    z
        .object({
        ...adminGuildEntitlementMutationBase,
        action: z.literal("set_manual_subscription"),
    })
        .strict(),
    z
        .object({
        ...adminGuildEntitlementMutationBase,
        action: z.literal("clear_manual_subscription"),
    })
        .strict(),
    z
        .object({
        ...adminGuildEntitlementMutationBase,
        action: z.literal("grant_complimentary"),
    })
        .strict(),
    z
        .object({
        ...adminGuildEntitlementMutationBase,
        action: z.literal("revoke_complimentary"),
    })
        .strict(),
]);
export const adminGuildOwnershipRecoverySchema = guildLeaderRecoverySchema;
export const adminAuditDisplaySchema = z
    .object({
    eventId: uuidSchema,
    actorUserId: discordSnowflakeSchema.nullable(),
    action: z.string().regex(/^[a-z0-9][a-z0-9_.:-]{0,99}$/),
    targetType: z.string().regex(/^[a-z0-9][a-z0-9_.:-]{0,99}$/),
    targetId: z.string().min(1).max(200),
    reason: adminMutationReasonSchema,
    changeSummary: sanitizedJsonObjectSchema(8_192),
    requestId: adminRequestIdSchema,
    createdAt: utcTimestampSchema,
})
    .strict();
export const adminAuditQuerySchema = z
    .object({
    cursor: opaqueCursorSchema.optional(),
    limit: z.int().min(1).max(50).default(50),
    guildId: discordSnowflakeSchema.optional(),
    actorUserId: discordSnowflakeSchema.optional(),
})
    .strict();
export const adminAuditPageSchema = z
    .object({
    events: z.array(adminAuditDisplaySchema).max(50),
    nextCursor: opaqueCursorSchema.nullable(),
})
    .strict();
export const adminGuildSupportSummarySchema = z
    .object({
    guildId: discordSnowflakeSchema,
    guildName: boundedNfcTextSchema(100, 512).nullable(),
    installed: z.boolean(),
    leaderUserId: discordSnowflakeSchema.nullable(),
    entitlementState: entitlementEffectiveStateSchema,
    entitlementRevision: z.int().positive(),
    wowauditValidationStatus: z.enum([
        "not_configured",
        "pending",
        "valid",
        "invalid",
        "revoked",
    ]),
    updatedAt: utcTimestampSchema,
})
    .strict();
export const adminGuildSupportQuerySchema = z
    .object({
    search: boundedNfcTextSchema(100, 512).optional(),
    cursor: opaqueCursorSchema.optional(),
    limit: z.int().min(1).max(50).default(50),
})
    .strict();
export const adminGuildSupportPageSchema = z
    .object({
    guilds: z.array(adminGuildSupportSummarySchema).max(50),
    nextCursor: opaqueCursorSchema.nullable(),
})
    .strict();
export const adminGuildSupportDetailSchema = z
    .object({
    summary: adminGuildSupportSummarySchema,
    ownership: guildOwnershipSchema,
    entitlement: guildEntitlementProjectionSchema,
    wowauditConnection: wowauditConnectionStatusSchema,
})
    .strict()
    .refine((value) => {
    const connectionStatus = value.wowauditConnection.connectionState === "not_configured"
        ? "not_configured"
        : value.wowauditConnection.validationStatus;
    return (value.summary.guildId === value.entitlement.guildId &&
        value.summary.guildId === value.ownership.guildId &&
        value.summary.guildId === value.wowauditConnection.guildId &&
        value.summary.leaderUserId ===
            (value.ownership.leader?.leaderUserId ?? null) &&
        value.summary.entitlementState === value.entitlement.effectiveState &&
        value.summary.entitlementRevision === value.entitlement.revision &&
        value.summary.wowauditValidationStatus === connectionStatus);
}, "Support detail records must contain a consistent guild projection");
export const adminWowauditRevocationSchema = z
    .object({
    expectedCredentialRevision: z.int().positive(),
    reason: adminMutationReasonSchema,
    requestId: adminRequestIdSchema,
})
    .strict();
//# sourceMappingURL=platform-admin.js.map